/**
 * SAMD wrapper for SlideImageSection that handles the scanner confirmation
 * popup and upload with scannerType via onFileAccepted callback.
 */
import { FC, useCallback, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { CloudUpload } from 'src/components/icons/cloud_upload';
import { RadioGroupField } from 'src/components/radio_group';
import { SlideImageSection } from 'src/distribution_platforms/features/components/slide_image_section';
import { useSlideScannerConfig } from 'src/distribution_platforms/features/hooks/use_slide_scanner_config';
import { useUpload } from 'src/hooks/use_upload';
import {
  SamdOrderDetailFieldsFragment,
  SlideImageScannerType,
} from 'src/types';
import { UploadedFileCategory } from 'src/types';

export interface SamdSlideImageSectionProps {
  order: SamdOrderDetailFieldsFragment;
}

export const SamdSlideImageSection: FC<SamdSlideImageSectionProps> = ({
  order,
}) => {
  const { uploadFile } = useUpload();
  const {
    instructionsPdfUrl,
    scannerOptions,
    validateScannerFileCompatibility,
  } = useSlideScannerConfig(order.specimenType);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [selectedScannerType, setSelectedScannerType] =
    useState<SlideImageScannerType | null>(null);

  const handleFileAccepted = useCallback((file: File) => {
    setSelectedFile(file);
    setSelectedScannerType(null);
    setUploadConfirmOpen(true);
  }, []);

  const validationError = useMemo(() => {
    if (!selectedFile || !isUploadConfirmOpen) return null;
    return validateScannerFileCompatibility(
      selectedFile.name,
      selectedScannerType,
    );
  }, [
    selectedFile,
    selectedScannerType,
    isUploadConfirmOpen,
    validateScannerFileCompatibility,
  ]);

  const handleUploadConfirm = useCallback(() => {
    if (selectedFile && selectedScannerType && !validationError) {
      uploadFile(
        selectedFile,
        order.id!,
        order.crossOrgId!,
        UploadedFileCategory.SlideImage,
        selectedScannerType,
      );
      setUploadConfirmOpen(false);
      setSelectedFile(null);
      setSelectedScannerType(null);
    }
  }, [
    order.id,
    order.crossOrgId,
    selectedFile,
    selectedScannerType,
    validationError,
    uploadFile,
  ]);

  const handleUploadCancel = useCallback(() => {
    setUploadConfirmOpen(false);
    setSelectedFile(null);
    setSelectedScannerType(null);
  }, []);

  return (
    <>
      <SlideImageSection order={order} onFileAccepted={handleFileAccepted} />
      <FeedbackPopup
        data={{
          title: 'Upload Slide Image and Verify Scanner Used',
          subtitle:
            'Please confirm that the image is high quality and from a supported scanner to start the file upload and AI processing.',
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          icon: CloudUpload,
          type: FeedbackType.Primary,
        }}
        width='386px'
        dialogTitleSx={{ padding: '0' }}
        isDialogOpen={isUploadConfirmOpen}
        onPrimaryButtonClick={handleUploadConfirm}
        onSecondaryButtonClick={handleUploadCancel}
        handleDialogClose={handleUploadCancel}
        validationError={validationError || undefined}
      >
        <Box
          sx={{
            mx: '-24px',
            mt: 2,
          }}
        >
          <RadioGroupField
            options={scannerOptions}
            value={selectedScannerType || ''}
            onChange={(event) =>
              setSelectedScannerType(
                event.target.value as SlideImageScannerType,
              )
            }
          />
          {instructionsPdfUrl && (
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                textAlign: 'left',
                ml: '10px',
              }}
            >
              Review{' '}
              <Typography
                component='span'
                variant='inherit'
                onClick={() => {
                  window.open(
                    instructionsPdfUrl,
                    '_blank',
                    'noopener,noreferrer',
                  );
                }}
                sx={{
                  display: 'inline',
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                Instructions for Use
              </Typography>{' '}
              for scanner settings
            </Typography>
          )}
        </Box>
      </FeedbackPopup>
    </>
  );
};
